// src/docs/DocsStakingLNCH.js
import React from "react";
import { Container, Typography } from "@mui/material";

const DocsStakingLNCH = () => {
  return (
    <Container maxWidth="md" sx={{ my: 4 }}>
      {/* Page Title */}
      <Typography variant="h5" sx={{ color: "#FFD700", mb: 3 }}>
        Staking LNCH: Pools &amp; Rewards
      </Typography>

      {/* Introduction */}
      <Typography variant="body1" sx={{ color: "#FFFFFF", mb: 3 }}>
        Staking LNCH allows you to lock up your LNCH tokens in one of three dedicated pools—Flex, Anchor, or Core—to earn rewards over time. This guide explains how the staking mechanism works on CrowdLaunch, details the fee structure, reward distribution, and outlines the functions provided by the CrowdLaunchPoolStaking smart contract.
      </Typography>

      {/* How Staking Works */}
      <Typography variant="h6" sx={{ color: "#FFD700", mb: 1 }}>
        How Staking Works
      </Typography>
      <Typography variant="body2" sx={{ color: "#FFFFFF", mb: 3 }}>
        When you stake LNCH tokens, you are depositing them into a pool by calling the <strong>stake</strong> function. The contract deducts a small fee (the stake fee) from your deposit and locks the remaining tokens in the pool. In return, you accumulate rewards over time based on:
        <br /><br />
        • Your share of the total staked tokens in the pool.
        <br />• The amount of rewards deposited into that pool.
        <br />• The duration for which your tokens remain staked.
      </Typography>

      {/* Pool Types & Their Characteristics */}
      <Typography variant="h6" sx={{ color: "#FFD700", mb: 1 }}>
        Pool Types &amp; Their Characteristics
      </Typography>
      <Typography variant="body2" sx={{ color: "#FFFFFF", mb: 3 }}>
        CrowdLaunch offers three staking pools:
        <br /><br />
        <strong>Flex Pool:</strong> 
        <br />- Designed for users who prefer flexibility with minimal lock-up times.
        <br />- Allocated a lower reward share (flexShare = 10%).
        <br /><br />
        <strong>Anchor Pool:</strong>
        <br />- Suited for medium-term staking with balanced rewards.
        <br />- Allocated a moderate reward share (anchorShare = 35%).
        <br /><br />
        <strong>Core Pool:</strong>
        <br />- Intended for long-term staking with higher reward potential.
        <br />- Allocated the largest reward share (coreShare = 55%).
      </Typography>

      {/* Fee Structure & Reward Calculation */}
      <Typography variant="h6" sx={{ color: "#FFD700", mb: 1 }}>
        Fee Structure &amp; Reward Calculation
      </Typography>
      <Typography variant="body2" sx={{ color: "#FFFFFF", mb: 3 }}>
        Each pool charges fees on both staking and unstaking:
        <br /><br />
        • <strong>Stake Fee:</strong> A percentage deducted when you stake your tokens. This fee is transferred to the collection wallet.
        <br />• <strong>Unstake Fee:</strong> A fee applied when you withdraw your staked tokens.
        <br />• <strong>Early Fee:</strong> May be applied if you unstake before the minimum staking period elapses.
        <br /><br />
        Rewards are distributed proportionally based on your staked amount and the pool’s total staked tokens. When the owner deposits rewards via the <strong>depositRewards</strong> function, the amount is split among the pools according to their shares (flexShare, anchorShare, and coreShare). Each staker's share of the rewards is calculated in real time as they accumulate over the period that their tokens remain staked.
      </Typography>

      {/* Claiming Rewards & Unstaking */}
      <Typography variant="h6" sx={{ color: "#FFD700", mb: 1 }}>
        Claiming Rewards &amp; Unstaking
      </Typography>
      <Typography variant="body2" sx={{ color: "#FFFFFF", mb: 3 }}>
        To claim your accumulated rewards, call the <strong>claimRewards</strong> function on the pool in which you are staking. Note that:
        <br /><br />
        • You can only claim rewards if the minimum staking time (minStakeTime) has elapsed since your last claim.
        <br />• Once claimed, your accumulated rewards are reset to zero.
        <br /><br />
        To exit a pool, use the <strong>unstake</strong> function. This will withdraw your staked tokens (minus the unstake fee) and update the pool’s total staked amount accordingly.
      </Typography>

      {/* Administrative & Emergency Functions */}
      <Typography variant="h6" sx={{ color: "#FFD700", mb: 1 }}>
        Administrative &amp; Emergency Functions
      </Typography>
      <Typography variant="body2" sx={{ color: "#FFFFFF", mb: 3 }}>
        The contract also includes several administrative functions:
        <br /><br />
        • <strong>updatePoolFees</strong> – Allows the owner to modify staking, unstaking, and early fees.
        <br />• <strong>updateRewardDistribution</strong> – Adjusts the reward share percentages for the pools.
        <br />• <strong>emergencyWithdraw</strong> and <strong>emergencyWithdrawBNB</strong> – Enable the owner to withdraw tokens or BNB in case of an emergency.
      </Typography>

      {/* Contract Overview */}
      <Typography variant="h6" sx={{ color: "#FFD700", mb: 1 }}>
        Contract Overview
      </Typography>
      <Typography variant="body2" sx={{ color: "#FFFFFF", mb: 3 }}>
        The CrowdLaunchPoolStaking contract is structured around two main data structures:
        <br /><br />
        • <strong>Stake:</strong> Stores individual staker data, including the amount staked, the last time rewards were claimed, and accumulated rewards.
        <br />• <strong>Pool:</strong> Maintains the overall data for a staking pool such as total tokens staked, reward balance, fee percentages, and a list of stakers.
        <br /><br />
        The contract uses helper functions like <strong>_updateReward</strong> to dynamically update rewards based on time elapsed and the staker’s share. This ensures that rewards are distributed fairly in real time.
      </Typography>

      {/* Conclusion */}
      <Typography variant="body1" sx={{ color: "#FFFFFF", mt: 4 }}>
        By staking LNCH, you contribute to the security and stability of the CrowdLaunch ecosystem while earning rewards proportional to your commitment. Before staking, be sure to review the fee structure and understand the terms associated with each pool. Always consider the risks and rewards carefully, and enjoy the benefits of a well-designed staking platform.
      </Typography>
    </Container>
  );
};

export default DocsStakingLNCH;
